import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Modal } from '@app/ui/modal';
import { Alert } from '@app/ui/alert';
import { error as toastError, success as toastSuccess } from '@app/snackbars';

import { COLOR_RUBRIC_PREVIEW_GET_ONE, MATCHING_RULES_PREVIEW_GET_ONE, RULES_PUBLISH } from '../gql';
import { ColorRubricPreview, MatchingRulesPreview, RuleFamily, RuleSetVersion } from '../types';
import { getGraphQLErrorMessage } from '../utils';

export interface PublishDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly version: RuleSetVersion;
  readonly ruleCount: number;
  readonly onPublished: (published: RuleSetVersion) => void;
}

// One mutation, no gate, no diff preview (SPEC D8/D9): the server validates the draft, writes the
// immutable artifact and flips the version's status in a single call. For the colour family, PLAN
// P11 additionally shows the derived reachable range and D4's blocking / D11-PG2's warning findings
// BEFORE the click — `rulesPublish` independently re-runs the SAME validator, so this query is only
// the UX, never the gate.
export const PublishDialog = ({ open, onClose, version, ruleCount, onPublished }: PublishDialogProps) => {
  const { t } = useTranslation('common');

  const isColorCoding = version.family === RuleFamily.colorCoding;
  const isMatching = version.family === RuleFamily.matching;

  const { data: previewData, loading: previewLoading } = useQuery<{
    colorRubricPreviewGetOne: ColorRubricPreview;
  }>(COLOR_RUBRIC_PREVIEW_GET_ONE, {
    variables: { ruleSetVersionId: version.id },
    skip: !isColorCoding,
    fetchPolicy: 'network-only',
  });
  const preview = previewData?.colorRubricPreviewGetOne;

  // PLAN P2-6 — the matching preview's weights total and V-1's WARN, never a block; V-2…V-8's
  // `blocking` findings disable Publish exactly like the colour family's.
  const { data: matchingPreviewData, loading: matchingPreviewLoading } = useQuery<{
    matchingRulesPreviewGetOne: MatchingRulesPreview;
  }>(MATCHING_RULES_PREVIEW_GET_ONE, {
    variables: { ruleSetVersionId: version.id },
    skip: !isMatching,
    fetchPolicy: 'network-only',
  });
  const matchingPreview = matchingPreviewData?.matchingRulesPreviewGetOne;

  const blocked = (isColorCoding && !!preview?.blocking.length) || (isMatching && !!matchingPreview?.blocking.length);

  const [publish, { loading }] = useMutation<{ rulesPublish: RuleSetVersion }>(RULES_PUBLISH);

  const handlePublish = async () => {
    try {
      const { data } = await publish({ variables: { input: { ruleSetVersionId: version.id } } });

      if (data?.rulesPublish) {
        toastSuccess(t('rules.publish.successMessage', { version: data.rulesPublish.version }));
        onPublished(data.rulesPublish);
      }
    } catch (e) {
      toastError(getGraphQLErrorMessage(e) ?? t('general.error.somethingWentWrong'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('rules.publish.dialogTitle', { family: t(`rules.family.${version.family}`), version: version.version })}
    >
      <Typography variant="body1">{t('rules.publish.explanation', { count: ruleCount })}</Typography>

      {isColorCoding && !previewLoading && preview && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('rules.colorCoding.preview.rangeLabel', { min: preview.minScore, max: preview.maxScore })}
          </Typography>

          {preview.blocking.map(message => (
            <Alert key={message} severity="error" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}

          {preview.warnings.map(message => (
            <Alert key={message} severity="warning" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}
        </Box>
      )}

      {isMatching && !matchingPreviewLoading && matchingPreview && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('rules.matching.preview.weightTotalLabel', { total: matchingPreview.activeWeightTotal })}
          </Typography>

          {matchingPreview.blocking.map(message => (
            <Alert key={message} severity="error" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}

          {matchingPreview.warnings.map(message => (
            <Alert key={message} severity="warning" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        {t('rules.publish.immutabilityNotice')}
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={onClose}>{t('rules.publish.cancelButton')}</Button>
        <LoadingButton
          loading={loading || (isColorCoding && previewLoading) || (isMatching && matchingPreviewLoading)}
          disabled={blocked}
          variant="contained"
          color="primary"
          onClick={handlePublish}
        >
          {t('rules.publish.confirmButton')}
        </LoadingButton>
      </Box>
    </Modal>
  );
};
